/***
 * 객실 예약리스트 조회
 */

import { useTranslation } from "react-i18next";
import { useResvCancelContext, useResvListContext } from "./context";
import { ReactNode, useEffect, useRef, useState } from "react";
import { IPageNav } from "interface/index";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  rmResvCnlApi,
  rmResvCnlFeeAmtApi,
  rmResvInfApi,
  rmResvsApi,
} from "components/api/rm";
import { apifield } from "components/utils/comm";
import { getToday, pageInitInf } from "components/utils/comm";
import useListRowInfStroe from "store/listRowInf";
import { IResvInf, IResvPayInf, IResvs } from "interface/rm/rsv";
import useAlertStore from "store/alert";
import usePageNoStore from "store/pageNo";
import { PAGE_ID_ROOM_RESV } from "components/constent";
import { initialPageNav } from "components/utils/comm";

export const useSearchList = () => {
  const { t } = useTranslation();
  const { isSuccess, setIsSuccess, param } = useResvListContext();

  const [rowData, setRowData] = useState<any[] | null>(null);
  const pageNavRef = useRef<IPageNav>(initialPageNav);
  const today = getToday();

  const mutation = useMutation({
    mutationKey: ["room-resv-search"],
    mutationFn: rmResvsApi,
    onSuccess: (res) => {
      if (res) {
        const {
          status: { code, message },
          data,
        } = res;
        if (code === 0) {
          //예약리스트 조회
          pageNavRef.current = data.pageNav;
          const pageNav = data.pageNav;
          const rmResvList = data.rmResvList.map(
            (resv: IResvs, index: number) => {
              return {
                No: (pageNav.pageNo - 1) * pageNav.pageRows + index + 1,
                ...resv,
                resvMemNm:
                  (resv.resvMemSurnm ?? "") + (resv.resvMemFstnm ?? ""),
                resvCstNm:
                  (resv.resvCstSurnm ?? "") + (resv.resvCstFstnm ?? ""),
                // resvStaCd: resv.rmPdResvDt > today ? resv.resvStaCd : "",
                resvStatus: resv.sellDt > today ? "wait" : "done",
                resvStaCdNm:
                  resv.sellDt > today
                    ? resv.resvStaCd === "RC00"
                      ? t("status_resv_cnl")
                      : resv.resvStaCd === "RS00"
                      ? t("status_resv")
                      : t("status_resv_done")
                    : t("status_resv_done"),
                modify: t("txt_edit_cmm"),
              };
            }
          );
          setRowData(rmResvList);
        }
        if (code === -1) {
          apifield(message);
          setIsSuccess(false);
        }
      }
    },
  });

  useEffect(() => {
    if (param) {
      mutation.mutate({
        ...param,
        ...pageInitInf,
      });
    }
  }, [param]);

  useEffect(() => {
    if (isSuccess) {
      mutation.mutate({
        ...param,
        ...pageInitInf,
      });
    }
  }, [isSuccess]);

  // NextPage
  const pageNo = usePageNoStore((state) => state.pageNo);
  const isCurrent = pageNo?.target === PAGE_ID_ROOM_RESV;

  useEffect(() => {
    if (isCurrent) {
      mutation.mutate({
        ...param,
        pageNo: pageNo?.no || 1,
        pageRows: pageNo?.pageRows,
      });
    }
  }, [pageNo?.no, pageNo?.pageRows]);

  return {
    rowData,
    pageNav: pageNavRef.current,
  };
};

// 예약 상세
export const useGetData = () => {
  const listRowInf = useListRowInfStroe((state) => state.listRowInf);

  const resvInfRef = useRef<IResvInf | null>(null);
  const payInfRef = useRef<IResvPayInf | null>(null);

  const rmPdNo = listRowInf.no as string;

  const query = useQuery({
    queryKey: ["room-resv-detail", listRowInf.no],
    queryFn: () => {
      return rmResvInfApi(rmPdNo);
    },
    select: (res) => {
      if (res) {
        const {
          status: { code, message },
          data,
        } = res;
        if (code === 0) {
          resvInfRef.current = data.rmResvDtl;
          payInfRef.current = data.resvPayDtl;
        }
        if (code === -1) {
          apifield(message);
        }
      }
    },
    enabled: !!rmPdNo,
  });

  // const mutation = useMutation({
  //   mutationKey: ["room-resv-detail"],
  //   mutationFn: rmResvInfApi,
  //   onSuccess: (res) => {
  //     if (res) {
  //       const {
  //         status: { code, message },
  //         data,
  //       } = res;
  //       if (code === 0) {
  //         resvInfRef.current = data.rmResvDtl;
  //         payInfRef.current = data.resvPayDtl;
  //       }
  //       if (code === -1) {
  //         apifield(message);
  //       }
  //     }
  //   },
  // });

  // useEffect(() => {
  //   if (!!rmPdNo) {
  //     mutation.mutate(rmPdNo);
  //   }
  // }, [rmPdNo]);

  return {
    resvInf: resvInfRef.current,
    payInf: payInfRef.current,
    isLoading: query.isPending,
    isSuccess: query.isSuccess,
  };
};

// 예약취소 수수료 조회
export const useGetCancelFeeData = (rmPdNo: string | undefined) => {
  const cancelFeeRef = useRef<any>(null);

  const mutate = useMutation({
    mutationKey: ["room-cancel-resv-fee"],
    mutationFn: rmResvCnlFeeAmtApi,
    onSuccess: (res) => {
      if (res) {
        const {
          status: { code, message },
          data,
        } = res;
        if (code === 0) {
          // 취소 수수료 확인
          cancelFeeRef.current = data;
        }
        if (code === -1) {
          apifield(message);
        }
      }
    },
  });

  useEffect(() => {
    if (!rmPdNo) return;
    mutate.mutate({ rmPdNo });
  }, []);

  return {
    cancelFee: cancelFeeRef.current,
  };
};

// 예약취소
export const useCancelResvMutate = () => {
  const { t } = useTranslation();
  const listRowInf = useListRowInfStroe((state) => state.listRowInf);
  const queryClicent = useQueryClient();
  const { setOpenResvCancelModal } = useResvCancelContext();
  const onSetAlert = useAlertStore((state) => state.onSetAlert);
  return useMutation({
    mutationFn: rmResvCnlApi,
    onSuccess: (res) => {
      if (res) {
        const {
          status: { code, message },
        } = res;

        if (code === 0) {
          onSetAlert({
            isOpen: true,
            inner: t("alt_resv_cnl_done"),
            onSetConfirm: () => {
              queryClicent.invalidateQueries({
                queryKey: ["room-resv-detail", listRowInf.no],
              });
              // 모달창 닫기
              setOpenResvCancelModal(false);
            },
          });
        }
        if (code === -1) {
          apifield(message);
        }
      }
    },
  });
};

type ICancelResv = {
  rmPdNo: string | undefined;
  inner: string;
};
export const useCancelResv = () => {
  const onSetAlert = useAlertStore((state) => state.onSetAlert);
  const { mutate } = useCancelResvMutate();
  const onCancelResv = ({ rmPdNo, inner }: ICancelResv) => {
    if (!rmPdNo) return;
    onSetAlert({
      isOpen: true,
      innerHtml: inner,
      cnclBtn: true,
      onSetConfirm: () => {
        mutate({ rmPdNo });
      },
    });
  };

  return {
    onCancelResv,
  };
};
